import styled from "styled-components";
import {useEffect, useRef, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import {agent} from "../../api/agent";
import {convertFromFinal, PipelineElement} from "../pipeline-builder/PipelineConfig";
import {findElement} from "../utils/PipelineHelper";
import PipelineTree from "../pipeline-builder/PipelineTree";
import Loading from "../Loading";
import Status from "../Status";

const Container = styled.div`
    display: flex;
    justify-content: center;
    margin: 1rem 0;

    .box {
        width: 80%;
        background-color: var(--content-box-bg);
        box-shadow: rgba(0, 0, 0, 0.24) 0px 3px 8px;
    }

    .header {
        padding: 1rem 2rem;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .header a {
        cursor: pointer;
        color: var(--text-secondary);
    }

    .header .actions {
        display: flex;
        align-items: center;
        gap: 1rem;
    }

    .header h1 {
        margin: .5rem 0 0 0;
        font-family: monospace;
        font-size: 1.1rem;
    }

    .tree {
        padding: 1rem;
        max-height: 70vh;
        overflow-y: auto;
    }

    .not-found {
        text-align: center;
        padding: 3rem;
        opacity: .7;
    }
`;

const POLL_INTERVAL_MS = 3000;

/**
 * Live view of one "Simulation" (MCP session): bootstraps its pipeline tree from
 * GET /simulation/{id} (same shape as any other skeleton.toJson()), then patches it live from
 * the /simulation/logs/{id} subscription - buffering/reducer logic mirrored from
 * JobLogs.tsx (initial/status/log messages applied onto the tree via findElement).
 */
export default function SimulationDetail() {
    const [searchParams] = useSearchParams();
    const id = searchParams.get('id') ?? '';
    const navigate = useNavigate();

    const [ci, setCi] = useState<PipelineElement | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [notFound, setNotFound] = useState<boolean>(false);
    const [repo, setRepo] = useState<string | undefined>(undefined);
    const treeRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!id) return;
        let cancelled = false;
        let hasLoaded = false;

        setLoading(true);
        setNotFound(false);
        setCi(null);

        const load = () => {
            agent.getSimulation(id).then(data => {
                if (cancelled) return;
                hasLoaded = true;
                setRepo(data.repo);
                convertFromFinal(data.pipeline).then(elements => {
                    if (cancelled) return;
                    setCi(elements[0]);
                    setNotFound(false);
                    setLoading(false);
                });
            }).catch(() => {
                if (cancelled) return;
                setNotFound(true);
                setLoading(false);
            });
        };

        load();

        // GetSimulation 404s until the MCP client actually calls create_pipeline, and this
        // effect otherwise only re-runs when `id` changes - without a retry, a tab opened on
        // a session that doesn't have a pipeline yet (or opened while one was just created)
        // showed "not found" forever, never noticing once the AI actually started running
        // something. Stop retrying once the structure has loaded once: from then on the
        // /simulation/logs subscription below keeps status/logs live for that pipeline.
        const interval = setInterval(() => {
            if (hasLoaded) {
                clearInterval(interval);
                return;
            }
            load();
        }, POLL_INTERVAL_MS);

        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [id]);

    useEffect(() => {
        if (!id || loading || notFound) {
            return;
        }

        let buffer: any[] = [];
        const interval = setInterval(() => {
            let buffer2: any[] = [...buffer];
            buffer = buffer.slice(buffer2.length, buffer2.length);

            let current: PipelineElement = ci!;
            for (const data of buffer2) {
                switch (data.type) {
                    case 'initial': {
                        const element = findElement(data.id, current);
                        if (!element) break;
                        element.lastStatus = data.status;
                        element.logs = data.logs;
                        break;
                    }
                    case 'status': {
                        const element = findElement(data.pipelineId, current);
                        if (!element) break;
                        element.lastStatus = data.status;
                        break;
                    }
                    case 'log': {
                        const element = findElement(data.pipelineId, current);
                        if (!element) break;
                        if (!element.logs) {
                            element.logs = [];
                        }
                        element.logs.push({level: data.level, message: data.message});
                        break;
                    }
                }
            }
            if (buffer2.length > 0) {
                setCi(JSON.parse(JSON.stringify(current)));
            }
        }, 25);

        const callback = (data: any) => {
            buffer.push(data);
        };

        const subId = agent.subscribe('/simulation/logs/' + id, callback);

        return () => {
            agent.unsubscribe(subId);
            clearInterval(interval);
        };
    }, [id, loading, notFound]);

    if (!id) {
        return (
            <Container>
                <div className={'box not-found'}>No simulation selected.</div>
            </Container>
        );
    }

    const abortSimulation = () => {
        agent.deleteSimulation(id).then(() => {
            navigate('/simulations');
        });
    };

    const overallStatus = ci?.lastStatus === 'running' ? 'running'
        : ci?.lastStatus === 'error' ? 'error'
            : ci?.lastStatus === 'warning' ? 'warning'
                : ci?.lastStatus === 'finished' ? 'finished'
                    : 'waiting';

    return (<>
        <Loading open={loading}></Loading>
        <Container>
            <div className={'box'}>
                <div className={'header'}>
                    <div>
                        <a onClick={() => navigate('/simulations')}>&larr; Back to Simulations</a>
                        <h1>{id}</h1>
                        {repo && <span>{repo}</span>}
                    </div>
                    {!notFound && ci && (
                        <div className={'actions'}>
                            <a onClick={abortSimulation}>Abort</a>
                            <div style={{position: 'relative'}}>
                                <Status status={overallStatus}></Status>
                            </div>
                        </div>
                    )}
                </div>
                {notFound && (
                    <div className={'not-found'}>This simulation no longer exists - it may have been
                        deconstructed, or the backend restarted.</div>
                )}
                {!notFound && (
                    <div className={'tree'} ref={treeRef}>
                        {ci && (
                            <PipelineTree current={ci} active={null} isNotTest={true}
                                          htmlElement={treeRef.current}
                                          isRunning={ci.lastStatus === 'running'}></PipelineTree>
                        )}
                    </div>
                )}
            </div>
        </Container>
    </>);
}
